import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { ReceiptQrCode } from '@/features/receipts/components/ReceiptQrCode';
import { getRoomToken } from '../data/votingRepository';
import { votingPublicUrl } from '../utils/publicUrl';

interface RoomQrProps {
  electionId: string;
  size?: number;
  /** Show the link under the code (for admins) */
  showLink?: boolean;
}

/**
 * QR of the public voting page with the rotating room token inside: a token works for 3–6 minutes
 * (time for an elderly voter to scan and type), so a photo of it sent by WhatsApp stops working soon
 * after. The QR changes every 3 minutes; a new token is requested just before the current one ends.
 */
export function RoomQr({ electionId, size = 280, showLink = false }: RoomQrProps) {
  const fetchedAt = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['voting', 'room-token', electionId],
    queryFn: async () => {
      const t = await getRoomToken(electionId);
      fetchedAt.current = Date.now();
      return t;
    },
    refetchInterval: 15_000,
  });

  // Countdown to the next rotation, against the server clock
  useEffect(() => {
    if (!data) return;
    const id = setInterval(() => {
      const drift = new Date(data.server_now).getTime() - fetchedAt.current;
      const left = Math.ceil((new Date(data.expires_at).getTime() - (Date.now() + drift)) / 1000);
      setSecondsLeft(Math.max(0, left));
      if (left <= 3) void refetch();
    }, 1000);
    return () => clearInterval(id);
  }, [data, refetch]);

  if (isLoading) return <Loader2 className="size-8 animate-spin text-muted-foreground" />;
  if (error || !data) return <p className="text-sm text-destructive">No se pudo generar el código.</p>;

  const url = votingPublicUrl(electionId, data.token);
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="rounded-2xl bg-white p-3 shadow">
        <ReceiptQrCode url={url} size={size} />
      </div>
      {secondsLeft !== null && (
        <p className="text-sm text-muted-foreground">
          El código cambia en {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
        </p>
      )}
      {showLink && <p className="max-w-full break-all text-center text-xs text-muted-foreground">{url}</p>}
    </div>
  );
}
